import { createRoute } from '@tanstack/react-router';
import { Route as authedRoute } from '../_authed';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import EditIcon from '@mui/icons-material/Edit';
import SecurityIcon from '@mui/icons-material/Security';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listUsers,
  createUser,
  updateUser,
  deactivateUser,
  getUserAccess,
  setUserAccess,
  listMailboxesAdmin,
  createMailbox,
  updateMailbox,
  deactivateMailbox,
  testMailbox,
  getFail2banStatus,
  banIp,
  unbanIp,
  type AdminUser,
  type AccessRow,
  type AdminMailbox,
} from '@/features/admin';

function AdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [snack, setSnack] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pwUser, setPwUser] = useState<AdminUser | null>(null);
  const [nameUser, setNameUser] = useState<AdminUser | null>(null);
  const [aclUser, setAclUser] = useState<AdminUser | null>(null);

  const usersQ = useQuery({ queryKey: ['admin-users'], queryFn: listUsers });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-users'] });

  const patchMut = useMutation({
    mutationFn: ({ id, ...p }: { id: number } & Record<string, unknown>) => updateUser(id, p),
    onSuccess: invalidate,
    onError: () => setSnack(t('mail.admin.error')),
  });
  const deactivateMut = useMutation({
    mutationFn: (id: number) => deactivateUser(id),
    onSuccess: invalidate,
    onError: () => setSnack(t('mail.admin.error')),
  });

  if (usersQ.isLoading) {
    return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }
  if (usersQ.isError) {
    const status = isAxiosError(usersQ.error) ? usersQ.error.response?.status : undefined;
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity={status === 403 ? 'warning' : 'error'}>
          {status === 403 ? t('mail.admin.noAccess') : t('mail.admin.error')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 980, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h2" component="h1">{t('mail.admin.title')}</Typography>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>{t('mail.admin.newUser')}</Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('mail.admin.email')}</TableCell>
            <TableCell>{t('mail.admin.name')}</TableCell>
            <TableCell>{t('mail.admin.admin')}</TableCell>
            <TableCell>{t('mail.admin.status')}</TableCell>
            <TableCell align="right">{t('mail.admin.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {usersQ.data?.map((u) => (
            <TableRow key={u.id} hover>
              <TableCell>{u.email}</TableCell>
              <TableCell>{u.display_name}</TableCell>
              <TableCell>
                <Switch
                  checked={u.is_admin}
                  onChange={(e) => patchMut.mutate({ id: u.id, is_admin: e.target.checked })}
                />
              </TableCell>
              <TableCell>
                {u.is_active ? (
                  <Chip size="small" color="success" label={t('mail.admin.active')} />
                ) : (
                  <Chip size="small" label={t('mail.admin.inactive')} />
                )}
              </TableCell>
              <TableCell align="right">
                <Tooltip title={t('mail.admin.editName')}>
                  <IconButton size="small" onClick={() => setNameUser(u)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('mail.admin.editAcl')}>
                  <span>
                    <IconButton size="small" onClick={() => setAclUser(u)} disabled={u.is_admin}>
                      <SecurityIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('mail.admin.resetPw')}>
                  <IconButton size="small" onClick={() => setPwUser(u)}>
                    <VpnKeyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {u.is_active ? (
                  <Tooltip title={t('mail.admin.deactivate')}>
                    <IconButton size="small" color="error" onClick={() => deactivateMut.mutate(u.id)}>
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('mail.admin.activate')}>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => patchMut.mutate({ id: u.id, is_active: true })}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
        {t('mail.admin.adminHint')}
      </Typography>

      <MailboxesSection onSnack={setSnack} />

      <Fail2banSection onSnack={setSnack} />


      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); invalidate(); }} onError={() => setSnack(t('mail.admin.error'))} />}
      {pwUser && <PasswordDialog user={pwUser} onClose={() => setPwUser(null)} onDone={() => { setPwUser(null); setSnack(t('mail.admin.pwChanged')); }} onError={() => setSnack(t('mail.admin.error'))} />}
      {nameUser && <NameDialog user={nameUser} onClose={() => setNameUser(null)} onDone={() => { setNameUser(null); invalidate(); }} onError={() => setSnack(t('mail.admin.error'))} />}
      {aclUser && <AclDialog user={aclUser} onClose={() => setAclUser(null)} onDone={() => setAclUser(null)} onError={() => setSnack(t('mail.admin.error'))} />}

      {snack && <Alert severity="info" onClose={() => setSnack(null)} sx={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)' }}>{snack}</Alert>}
    </Box>
  );
}

function Fail2banSection({ onSnack }: { onSnack: (msg: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [ipInput, setIpInput] = useState('');
  const statusQ = useQuery({
    queryKey: ['fail2ban'],
    queryFn: getFail2banStatus,
    refetchInterval: 30000,
  });
  const invalidate = (s?: unknown) => { if (s) qc.setQueryData(['fail2ban'], s); else qc.invalidateQueries({ queryKey: ['fail2ban'] }); };

  const banMut = useMutation({
    mutationFn: (ip: string) => banIp(ip),
    onSuccess: (s) => { invalidate(s); setIpInput(''); onSnack(t('mail.f2b.banned')); },
    onError: (e) => onSnack(isAxiosError(e) ? (e.response?.data?.detail ?? t('mail.f2b.error')) : t('mail.f2b.error')),
  });
  const unbanMut = useMutation({
    mutationFn: (ip: string) => unbanIp(ip),
    onSuccess: (s) => { invalidate(s); onSnack(t('mail.f2b.unbanned')); },
    onError: (e) => onSnack(isAxiosError(e) ? (e.response?.data?.detail ?? t('mail.f2b.error')) : t('mail.f2b.error')),
  });

  const ipValid = /^(\d{1,3}\.){3}\d{1,3}$|^[0-9a-fA-F:]+:[0-9a-fA-F:]+$/.test(ipInput.trim());
  const st = statusQ.data;

  return (
    <Box sx={{ mt: 5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <SecurityIcon fontSize="small" />
        <Typography variant="h2" component="h2">{t('mail.f2b.title')}</Typography>
        <Tooltip title={t('mail.f2b.refresh')}>
          <IconButton size="small" onClick={() => invalidate()}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>

      {st && !st.available && (
        <Alert severity="warning" sx={{ mb: 2 }}>{t('mail.f2b.unavailable')}{st.error ? `: ${st.error}` : ''}</Alert>
      )}
      {st && st.available && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
          <Chip color="error" label={`${t('mail.f2b.currentlyBanned')}: ${st.currently_banned}`} />
          <Chip variant="outlined" label={`${t('mail.f2b.totalBanned')}: ${st.total_banned}`} />
          <Chip variant="outlined" label={`${t('mail.f2b.totalFailed')}: ${st.total_failed}`} />
        </Stack>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" gap={1}>
        <TextField
          size="small"
          label={t('mail.f2b.ipLabel')}
          placeholder="203.0.113.42"
          value={ipInput}
          onChange={(e) => setIpInput(e.target.value)}
          error={!!ipInput && !ipValid}
          helperText={ipInput && !ipValid ? t('mail.f2b.ipInvalid') : ' '}
          sx={{ minWidth: 220 }}
        />
        <Button
          variant="contained"
          color="error"
          startIcon={<BlockIcon />}
          disabled={!ipValid || banMut.isPending}
          onClick={() => { if (window.confirm(t('mail.f2b.confirmBan', { ip: ipInput.trim() }))) banMut.mutate(ipInput.trim()); }}
        >
          {t('mail.f2b.ban')}
        </Button>
      </Stack>

      {st && st.available && (
        st.banned_ips.length === 0 ? (
          <Typography variant="body2" color="textSecondary">{t('mail.f2b.noneBanned')}</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('mail.f2b.bannedIp')}</TableCell>
                <TableCell align="right">{t('mail.admin.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {st.banned_ips.map((ip) => (
                <TableRow key={ip} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{ip}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('mail.f2b.unban')}>
                      <IconButton size="small" color="success" disabled={unbanMut.isPending} onClick={() => unbanMut.mutate(ip)}>
                        <LockOpenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      )}
      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
        {t('mail.f2b.hint')}
      </Typography>
    </Box>
  );
}

function MailboxesSection({ onSnack }: { onSnack: (msg: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; mb?: AdminMailbox } | null>(null);
  const mboxQ = useQuery({ queryKey: ['admin-mailboxes'], queryFn: listMailboxesAdmin });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-mailboxes'] });

  const deactivateMut = useMutation({ mutationFn: (id: number) => deactivateMailbox(id), onSuccess: invalidate, onError: () => onSnack(t('mail.admin.error')) });
  const activateMut = useMutation({ mutationFn: (id: number) => updateMailbox(id, { is_active: true }), onSuccess: invalidate, onError: () => onSnack(t('mail.admin.error')) });
  const testMut = useMutation({
    mutationFn: (id: number) => testMailbox(id),
    onSuccess: (r) => onSnack(r.ok ? t('mail.admin.testOk') : `${t('mail.admin.testFail')}: ${r.detail ?? ''}`),
    onError: () => onSnack(t('mail.admin.error')),
  });

  return (
    <Box sx={{ mt: 5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h2" component="h2">{t('mail.admin.mailboxes')}</Typography>
        <Button variant="contained" onClick={() => setDialog({ mode: 'create' })}>{t('mail.admin.newMailbox')}</Button>
      </Stack>
      {mboxQ.isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('mail.admin.address')}</TableCell>
            <TableCell>{t('mail.admin.name')}</TableCell>
            <TableCell>IMAP / SMTP</TableCell>
            <TableCell>{t('mail.admin.status')}</TableCell>
            <TableCell align="right">{t('mail.admin.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {mboxQ.data?.map((m) => (
            <TableRow key={m.id} hover>
              <TableCell>{m.address}</TableCell>
              <TableCell>{m.display_name}</TableCell>
              <TableCell><Typography variant="caption" color="textSecondary">{m.imap_host}:{m.imap_port} / {m.smtp_host}:{m.smtp_port}</Typography></TableCell>
              <TableCell>{m.is_active ? <Chip size="small" color="success" label={t('mail.admin.active')} /> : <Chip size="small" label={t('mail.admin.inactive')} />}</TableCell>
              <TableCell align="right">
                <Button size="small" disabled={testMut.isPending} onClick={() => testMut.mutate(m.id)}>{t('mail.admin.test')}</Button>
                <Tooltip title={t('mail.admin.edit')}><IconButton size="small" onClick={() => setDialog({ mode: 'edit', mb: m })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                {m.is_active ? (
                  <Tooltip title={t('mail.admin.deactivate')}><IconButton size="small" color="error" onClick={() => deactivateMut.mutate(m.id)}><BlockIcon fontSize="small" /></IconButton></Tooltip>
                ) : (
                  <Tooltip title={t('mail.admin.activate')}><IconButton size="small" color="success" onClick={() => activateMut.mutate(m.id)}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {dialog && (
        <MailboxDialog
          mode={dialog.mode}
          mb={dialog.mb}
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); invalidate(); }}
          onError={() => onSnack(t('mail.admin.error'))}
        />
      )}
    </Box>
  );
}

function MailboxDialog({ mode, mb, onClose, onDone, onError }: { mode: 'create' | 'edit'; mb?: AdminMailbox; onClose: () => void; onDone: () => void; onError: () => void }) {
  const { t } = useTranslation();
  const [address, setAddress] = useState(mb?.address ?? '');
  const [name, setName] = useState(mb?.display_name ?? '');
  const [password, setPassword] = useState('');
  const [imapHost, setImapHost] = useState(mb?.imap_host ?? '127.0.0.1');
  const [imapPort, setImapPort] = useState(mb?.imap_port ?? 993);
  const [smtpHost, setSmtpHost] = useState(mb?.smtp_host ?? '127.0.0.1');
  const [smtpPort, setSmtpPort] = useState(mb?.smtp_port ?? 587);
  const mut = useMutation({
    mutationFn: () =>
      mode === 'create'
        ? createMailbox({ address, display_name: name, imap_password: password, imap_host: imapHost, imap_port: imapPort, smtp_host: smtpHost, smtp_port: smtpPort })
        : updateMailbox(mb!.id, { display_name: name, imap_host: imapHost, imap_port: imapPort, smtp_host: smtpHost, smtp_port: smtpPort, ...(password ? { imap_password: password } : {}) }),
    onSuccess: onDone,
    onError,
  });
  const ok = mode === 'create' ? address && name && password : !!name;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{mode === 'create' ? t('mail.admin.newMailbox') : `${t('mail.admin.edit')} — ${mb?.address}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {mode === 'create' && <TextField label={t('mail.admin.address')} value={address} onChange={(e) => setAddress(e.target.value)} size="small" fullWidth />}
          <TextField label={t('mail.admin.name')} value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth />
          <TextField label={t('mail.admin.imapPassword')} value={password} onChange={(e) => setPassword(e.target.value)} size="small" fullWidth helperText={mode === 'edit' ? t('mail.admin.pwKeep') : undefined} />
          <Stack direction="row" spacing={1}>
            <TextField label="IMAP Host" value={imapHost} onChange={(e) => setImapHost(e.target.value)} size="small" fullWidth />
            <TextField label="Port" type="number" value={imapPort} onChange={(e) => setImapPort(Number(e.target.value))} size="small" sx={{ width: 100 }} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField label="SMTP Host" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} size="small" fullWidth />
            <TextField label="Port" type="number" value={smtpPort} onChange={(e) => setSmtpPort(Number(e.target.value))} size="small" sx={{ width: 100 }} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('mail.compose.cancel')}</Button>
        <Button variant="contained" disabled={!ok || mut.isPending} onClick={() => mut.mutate()}>{t('mail.admin.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function CreateDialog({ onClose, onDone, onError }: { onClose: () => void; onDone: () => void; onError: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const mut = useMutation({
    mutationFn: () => createUser({ email, display_name: name, password, is_admin: isAdmin }),
    onSuccess: onDone,
    onError,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('mail.admin.newUser')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label={t('mail.admin.email')} value={email} onChange={(e) => setEmail(e.target.value)} size="small" fullWidth />
          <TextField label={t('mail.admin.name')} value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth />
          <TextField label={t('mail.admin.password')} value={password} onChange={(e) => setPassword(e.target.value)} size="small" fullWidth helperText={t('mail.admin.pwHint')} />
          <FormControlLabel control={<Switch checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />} label={t('mail.admin.admin')} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('mail.compose.cancel')}</Button>
        <Button variant="contained" disabled={mut.isPending || !email || !name || password.length < 8} onClick={() => mut.mutate()}>
          {t('mail.admin.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PasswordDialog({ user, onClose, onDone, onError }: { user: AdminUser; onClose: () => void; onDone: () => void; onError: () => void }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const mut = useMutation({ mutationFn: () => updateUser(user.id, { password }), onSuccess: onDone, onError });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('mail.admin.resetPw')} — {user.email}</DialogTitle>
      <DialogContent>
        <TextField autoFocus label={t('mail.admin.password')} value={password} onChange={(e) => setPassword(e.target.value)} size="small" fullWidth sx={{ mt: 1 }} helperText={t('mail.admin.pwHint')} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('mail.compose.cancel')}</Button>
        <Button variant="contained" disabled={mut.isPending || password.length < 8} onClick={() => mut.mutate()}>{t('mail.admin.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function NameDialog({ user, onClose, onDone, onError }: { user: AdminUser; onClose: () => void; onDone: () => void; onError: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(user.display_name);
  const mut = useMutation({ mutationFn: () => updateUser(user.id, { display_name: name }), onSuccess: onDone, onError });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('mail.admin.editName')} — {user.email}</DialogTitle>
      <DialogContent>
        <TextField autoFocus label={t('mail.admin.name')} value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth sx={{ mt: 1 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('mail.compose.cancel')}</Button>
        <Button variant="contained" disabled={mut.isPending || !name.trim()} onClick={() => mut.mutate()}>{t('mail.admin.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function AclDialog({ user, onClose, onDone, onError }: { user: AdminUser; onClose: () => void; onDone: () => void; onError: () => void }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AccessRow[] | null>(null);
  useQuery({
    queryKey: ['admin-acl', user.id],
    queryFn: async () => {
      const r = await getUserAccess(user.id);
      setRows(r);
      return r;
    },
  });
  const mut = useMutation({ mutationFn: () => setUserAccess(user.id, rows as AccessRow[]), onSuccess: onDone, onError });
  const set = (i: number, key: 'can_read' | 'can_reply' | 'can_send_as_customer', v: boolean) =>
    setRows((prev) =>
      prev!.map((r, idx) => {
        if (idx !== i) return r;
        const next = { ...r, [key]: v };
        // cascade: send-as ⇒ reply ⇒ read (you can't send-as without replying)
        if (v && key === 'can_send_as_customer') { next.can_reply = true; next.can_read = true; }
        if (v && key === 'can_reply') next.can_read = true;
        return next;
      })
    );
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('mail.admin.editAcl')} — {user.email}</DialogTitle>
      <DialogContent>
        {!rows ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('mail.mailbox.mailboxLabel')}</TableCell>
                <TableCell align="center">{t('mail.admin.read')}</TableCell>
                <TableCell align="center">{t('mail.admin.reply')}</TableCell>
                <TableCell align="center">{t('mail.admin.sendAs')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.mailbox_id}>
                  <TableCell>{r.display_name}<br /><Typography variant="caption" color="textSecondary">{r.address}</Typography></TableCell>
                  <TableCell align="center"><Checkbox checked={r.can_read} onChange={(e) => set(i, 'can_read', e.target.checked)} /></TableCell>
                  <TableCell align="center"><Checkbox checked={r.can_reply} onChange={(e) => set(i, 'can_reply', e.target.checked)} /></TableCell>
                  <TableCell align="center"><Checkbox checked={r.can_send_as_customer} onChange={(e) => set(i, 'can_send_as_customer', e.target.checked)} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('mail.compose.cancel')}</Button>
        <Button variant="contained" disabled={!rows || mut.isPending} onClick={() => mut.mutate()}>{t('mail.admin.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export const Route = createRoute({
  getParentRoute: () => authedRoute,
  path: 'admin',
  component: AdminPage,
});
